import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { PageHeader } from "../PageHeader";
import { api as convexApi } from "../../../lib/convexApi";

interface ExceptionItem {
  id: string;
  pattern?: string;
  scope?: string;
  cmd?: string;
  grantedBy?: string;
  grantedAt?: string;
  expiresAt?: string;
  reason?: string;
  revokedAt?: string;
}

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

function asIso(n: unknown): string | undefined {
  if (typeof n === "number" && Number.isFinite(n)) {
    return new Date(n).toISOString();
  }
  if (typeof n === "string" && n) return n;
  return undefined;
}

function coerce(raw: Record<string, unknown>, idx: number): ExceptionItem {
  // Convex shape: _id, pattern, reason, createdBy, expiresAt (number),
  // createdAt (number), deleted (boolean), deletedAt (number).
  return {
    id: typeof raw._id === "string" ? raw._id : typeof raw.id === "string" ? raw.id : `ex-${idx}`,
    pattern: firstString(raw, ["pattern", "commandPattern", "ruleId"]),
    scope: firstString(raw, ["scope"]),
    cmd: firstString(raw, ["cmd", "command"]),
    grantedBy: firstString(raw, ["createdBy", "grantedBy", "author"]),
    grantedAt: asIso(raw.createdAt) || firstString(raw, ["grantedAt", "recordedAt"]),
    expiresAt: asIso(raw.expiresAt) || firstString(raw, ["expires_at"]),
    reason: firstString(raw, ["reason"]),
    revokedAt: asIso(raw.deletedAt) || firstString(raw, ["revokedAt", "revoked_at"]),
  };
}

function timeLeft(iso: string | undefined): string {
  if (!iso) return "no expiry";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "no expiry";
  const ms = t - Date.now();
  if (ms <= 0) return "expired";
  const d = Math.floor(ms / (24 * 3600_000));
  if (d >= 1) return `expires in ${d}d`;
  const h = Math.floor(ms / 3600_000);
  if (h >= 1) return `expires in ${h}h`;
  const m = Math.floor(ms / 60_000);
  if (m >= 1) return `expires in ${m}m`;
  return "expires in <1m";
}

export function ExceptionsPage() {
  const [error, setError] = useState<string | null>(null);

  // Reactive snapshot — orgQuery soft-delete filter hides revoked rows
  // by default, so listExceptions only returns active (or future-revoked)
  // exceptions. Pattern #14: read directly, no local mirror state.
  const raw = useQuery((convexApi as any).policy.queries.listExceptions, { limit: 100 }) as
    | Array<Record<string, unknown>>
    | undefined;

  const revoke = useMutation(
    (convexApi as any).policy.mutations.revokeException
  ) as unknown as (args: { id: string }) => Promise<unknown>;

  const items = useMemo(() => (raw ?? []).map((row, i) => coerce(row, i)), [raw]);

  const loading = raw === undefined;

  async function onRevoke(id: string) {
    setError(null);
    try {
      await revoke({ id });
      // Reactive snapshot updates automatically — no manual reload needed.
    } catch (e) {
      setError(e instanceof Error ? e.message : "Revoke failed");
    }
  }

  const activeCount = items.filter((e) => {
    if (e.revokedAt) return false;
    if (!e.expiresAt) return true;
    const t = Date.parse(e.expiresAt);
    return Number.isNaN(t) || t > Date.now();
  }).length;

  return (
    <>
      <PageHeader
        title="Exceptions"
        subtitle={`${activeCount} active · live`}
        actions={
          <button
            type="button"
            className="sr-btn sr-btn--ghost"
            disabled={loading}
            onClick={() => void 0}
            title="Exceptions list updates live (Convex reactive)"
          >
            <RefreshCw size={14} /> Live
          </button>
        }
      />
      <div className="sr-page-body">
        {loading && <div className="sr-empty">Loading exceptions…</div>}
        {error && <div className="sr-empty">&#9888; {error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="sr-empty">No exceptions granted.</div>
        )}
        {items.map((e) => {
          const expiresT = e.expiresAt ? Date.parse(e.expiresAt) : NaN;
          const expired = !Number.isNaN(expiresT) && expiresT <= Date.now();
          const revoked = !!e.revokedAt;
          const active = !expired && !revoked;
          return (
            <article key={e.id} className="sr-exception">
              <div className="sr-exception__head">
                <strong className={active ? "is-active" : ""}>
                  {active ? "ACTIVE" : revoked ? "REVOKED" : "EXPIRED"}
                </strong>
                <code>{e.cmd ?? e.pattern ?? "—"}</code>
                <span className="sr-exception__time">{timeLeft(e.expiresAt)}</span>
              </div>
              <div className="sr-exception__meta">
                Granted by {e.grantedBy ?? "—"} &middot; {e.grantedAt ?? "—"}
                {e.reason ? ` · reason: "${e.reason}"` : ""}
              </div>
              <div className="sr-exception__meta">
                Pattern: {e.pattern ?? "—"} &middot; scope: {e.scope ?? "—"}
              </div>
              {active && (
                <div className="sr-exception__actions">
                  <button
                    type="button"
                    className="sr-btn sr-btn--ghost"
                    onClick={() => void onRevoke(e.id)}
                  >
                    Revoke now
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
}
